/**
 * Availability Utils
 *
 * Shared utility for evaluating availability rules on CMS components.
 * Used by CmsSwitcher (for switcher item disablement) and CustomReactComponent
 * (for hiding components based on market selection).
 *
 * DatoCMS JSON field format:
 * {
 *   "enabled": true/false,                    // Simple toggle
 *   "type": "market_based",                   // Rule type
 *   "disabledMarkets": ["5", "12"],           // Blacklist (hide for these markets)
 *   "enabledMarkets": ["1", "3", "7"],        // Whitelist (show only for these markets)
 *   "fallbackEnabled": true                   // Default when no rule matches
 * }
 */

/**
 * Availability rules for conditional visibility based on market/context
 */
export interface AvailabilityRules {
  enabled?: boolean;
  type?: 'market_based' | 'rule_based';
  disabledMarkets?: string[];
  enabledMarkets?: string[];
  fallbackEnabled?: boolean;
}

/**
 * Context for evaluating availability rules
 */
export interface AvailabilityContext {
  marketId?: string | number;
}

/**
 * Evaluate availability rules to determine if an item should be disabled/hidden
 * @param rules - Availability rules from CMS
 * @param context - Current context (market, etc.)
 * @returns true if item should be disabled/hidden
 */
export const evaluateAvailability = (
  rules: AvailabilityRules | undefined,
  context: AvailabilityContext
): boolean => {
  // No rules means enabled by default
  if (!rules) {
    return false;
  }

  // Simple enabled flag
  if (rules.enabled === false) {
    return true;
  }
  if (rules.enabled === true && !rules.type) {
    return false;
  }

  // Market-based rules
  if (rules.type === 'market_based') {
    const marketId = String(context.marketId || '');

    // Check disabled markets (blacklist takes precedence)
    if (rules.disabledMarkets?.includes(marketId)) {
      return true; // disabled
    }

    // Check enabled markets (whitelist)
    if (rules.enabledMarkets && rules.enabledMarkets.length > 0) {
      return !rules.enabledMarkets.includes(marketId); // disabled if not in whitelist
    }

    // Fallback
    return !(rules.fallbackEnabled ?? true);
  }

  return false;
};
